// Street addresses for the members on screen. The server reverse geocodes each
// member's latest fix (throttled and cached for everyone), so the browser never
// sends coordinates to a geocoder.
import { useEffect, useRef, useState } from "react";
import { api, ApiError, unwrap } from "../api";
import { AddressCache, addressKey } from "./addresses";

/** A lookup that did not resolve a member's position is retried no sooner than this. */
const RETRY_MS = 60_000;

const cache = new AddressCache();

export interface MemberPosition {
  userId: string;
  key: string;
}

export interface Addresses {
  /** False once the server reports that it has no geocoder. */
  enabled: boolean;
  /** The address at `key`: a string, null when there is none, undefined while unknown. */
  get: (key: string) => string | null | undefined;
}

/**
 * Looks up addresses for `positions` (see `addressKey`) in `circleId`. An
 * address is matched to the position the server looked up, so a member's old
 * street never labels a new position. `nowMs` paces retries.
 */
export function useAddresses(circleId: string, positions: readonly MemberPosition[], nowMs: number): Addresses {
  const [, setVersion] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const inflight = useRef(new Set<string>());
  /** When each member's lookup for a key last finished without resolving it. */
  const attempts = useRef(new Map<string, number>());
  const mounted = useRef(true);
  /** Each member's current key, for lookups that finish after they moved. */
  const current = useRef(new Map<string, string>());
  const wanted = positions.map((p) => `${p.userId}=${p.key}`).join(";");

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const entries = (wanted ? wanted.split(";") : []).map((entry) => entry.split("=") as [string, string]);
    current.current = new Map(entries);
    if (!enabled) return;
    for (const [userId, key] of entries) {
      const entry = `${userId}=${key}`;
      if (cache.get(key) !== undefined || inflight.current.has(userId)) continue;
      const attempted = attempts.current.get(entry);
      if (attempted !== undefined && Date.now() - attempted < RETRY_MS) continue;
      inflight.current.add(userId);
      void (async () => {
        try {
          const found = unwrap(
            await api.GET("/circles/{circleId}/members/{userId}/address", { params: { path: { circleId, userId } } }),
          );
          cache.put(addressKey(found.lat, found.lon), found.address);
        } catch (err) {
          if (err instanceof ApiError && err.code === "geocoding_disabled") {
            if (mounted.current) setEnabled(false);
            return;
          }
        } finally {
          inflight.current.delete(userId);
        }
        // The server answers for the latest fix, so the member may have moved
        // since; whatever key they are at now waits before it is retried.
        const now = current.current.get(userId);
        if (now !== undefined && cache.get(now) === undefined) {
          if (attempts.current.size >= 1_000) attempts.current.clear();
          attempts.current.set(`${userId}=${now}`, Date.now());
        }
        if (mounted.current) setVersion((v) => v + 1);
      })();
    }
  }, [circleId, wanted, nowMs, enabled]);

  return { enabled, get: (key) => (enabled ? cache.get(key) : undefined) };
}
